import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, toRef, watch } from 'vue'
import FormLayout from '../common/FormLayout.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'
import NewJobButton from '@/components/common/NewJobButton.vue'
import FieldViewReadonlyCustomData from './FieldViewReadonlyCustomData.vue'
import FieldViewLocationDetailsEdit from './FieldViewLocationDetailsEdit.vue'
import FieldViewLocationForm from './FieldViewLocationForm.vue'
import FieldViewAdjustmentForm from './FieldViewAdjustmentForm.vue'
import FieldViewLocationInventory from './FieldViewLocationInventory.vue'
import LocationDTO from '@/models/location'
import FieldViewLocationNotFound from './FieldViewLocationNotFound.vue'
import LocationDetailsJobTab from './LocationDetailsJobTab.vue'
import LocationDetailsSensorsTab from './LocationDetailsSensorsTab.vue'
import { useLocationQuery } from '@/api/locations'
import { useGetLocationTypesQuery } from '@/api/location-types'
import { useStore } from 'vuex-composition-helpers'
import { useMap } from './mapbox/context'
import { useFieldView } from './FieldViewState'
import { intersection } from 'lodash'
import { useSensorsForLocationQuery } from '@/api/sensors'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import VsText from '@/components/vision/VsText.vue'
import LocationTagsEditor from '@/components/common/tags/LocationTagsEditor.vue'
import TagsDisplay from '@/components/common/tags/TagsDisplay.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'FieldViewReadonlyLocation',
  props: {
    locationId: null
  },
  emits: ["dismiss"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'dismiss')
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  locationId: string | number | null
}





const locationIdRef = toRef(props, 'locationId')
const {
  data: location,
  refetch,
  isFetched,
  isLoading: isLoadingLocations,
} = useLocationQuery(locationIdRef)

const { data: sensorsList } = useSensorsForLocationQuery(locationIdRef)

const isBottomSheetVisible = computed(
  () =>
    showingEditingBottomSheet.value ||
    showingAdjustmentForm.value ||
    showTagsEditor.value
)

const { map } = useMap()
const { setEditMode, setSelectedLocation, upsertModifiedLocation } =
  useFieldView()
const showingEditingBottomSheet = ref<boolean>(false)
const showTagsEditor = ref(false)

watch(
  location,
  async (nextLocation) => {
    if (nextLocation) {
      if (showingEditingBottomSheet.value) {
        showingEditingBottomSheet.value = false
      }
      setSelectedLocation(nextLocation)
      flyToLocation(nextLocation)
    }
  },
  {
    immediate: true,
  }
)

const tabsModel = ref<any>()

const maxDescriptionLength = 60
const isShowingMore = ref<boolean>(false)

const hasDescriptionToggle = computed(() => {
  const desc = location?.value?.description || ''
  return desc.length > maxDescriptionLength
})

const toggleMore = () => {
  isShowingMore.value = !isShowingMore.value
}

const { data: locationTypesResults } = useGetLocationTypesQuery()
const locationType = computed(() =>
  locationTypesResults.value?.find(
    (x) => x.id === location.value?.locationTypeId
  )
)

const locationTypeCustomFieldsSchema = computed<object | null>(() => {
  if (locationType.value?.customFieldsSchema == null) {
    return null
  }
  return locationType.value?.customFieldsSchema as object
})

const locationTitle = computed<string>(() => {
  return location.value?.location ?? 'Location'
})

const store = useStore()

const canEditLocationDetails = computed<
  (locationOrgId?: string | null) => boolean
>(() => store.getters['permissions/canEditLocationDetails'])

const canFullyEditLocation = computed<
  (locationOrgId?: string | null) => boolean
>(() => store.getters['permissions/canFullyEditLocation'])

const accessibleHubIds = computed<string[]>(
  () => store.getters['permissions/getAccessibleHubIds']
)

const isLocationGrantedAccessToCurrentUserAccessibleHubs = computed(
  () =>
    intersection(
      accessibleHubIds.value,
      location.value?.accessBusinessUnitIds ?? []
    ).length > 0
)
const shouldFocusHubsSelector = ref(false)

const grantedAccessMessage = computed(() => {
  if (isLocationGrantedAccessToCurrentUserAccessibleHubs.value) {
    return
  }

  if (canFullyEditLocation.value(location.value?.ownerId)) {
    return 'Add this location to a hub in order to request jobs here.'
  } else {
    return (
      'This location must be granted access in order to request jobs here. ' +
      'Contact your IronSight administrator to gain access.'
    )
  }
})

const adjustmentFormAssetId = ref<number | null>(null)

const showingAdjustmentForm = computed<boolean>(() => {
  return adjustmentFormAssetId.value !== null
})

const handleEditingSheetDismiss = () => {
  showingEditingBottomSheet.value = false
  setEditMode(false)
  shouldFocusHubsSelector.value = false
}

const dismissAdjustmentForm = () => {
  adjustmentFormAssetId.value = null
}

const editInventory = (assetId: number) => {
  adjustmentFormAssetId.value = assetId
}

const handleEditingSheetSave = async () => {
  await refetch()
  setEditMode(false)
  upsertModifiedLocation(location.value!)
  showingEditingBottomSheet.value = false
  shouldFocusHubsSelector.value = false
}

const handleEditLocationClicked = () => {
  setEditMode(true)
  showingEditingBottomSheet.value = true
}

const canEditLocation = computed<boolean>(() => {
  return canEditLocationDetails.value(location.value?.ownerId)
})

const canOnlyEditLocationDetails = computed<boolean>(() => {
  return (
    canEditLocationDetails.value(location.value?.ownerId) &&
    !canFullyEditLocation.value(location.value?.ownerId)
  )
})

const handleDismiss = () => {
  if (!isBottomSheetVisible.value) {
    emits('dismiss')
  }
}

function flyToLocation(location: LocationDTO) {
  const coordinates = { lon: location.longitude!, lat: location.latitude! }
  map.value?.flyTo({
    center: coordinates,
    zoom: 16,
    animate: map.value?.getBounds().contains(coordinates),
  })
}

return { __sfc: true,props, emits, locationIdRef, location, refetch, isFetched, isLoadingLocations, sensorsList, isBottomSheetVisible, map, setEditMode, setSelectedLocation, upsertModifiedLocation, showingEditingBottomSheet, showTagsEditor, tabsModel, maxDescriptionLength, isShowingMore, hasDescriptionToggle, toggleMore, locationTypesResults, locationType, locationTypeCustomFieldsSchema, locationTitle, store, canEditLocationDetails, canFullyEditLocation, accessibleHubIds, isLocationGrantedAccessToCurrentUserAccessibleHubs, shouldFocusHubsSelector, grantedAccessMessage, adjustmentFormAssetId, showingAdjustmentForm, handleEditingSheetDismiss, dismissAdjustmentForm, editInventory, handleEditingSheetSave, handleEditLocationClicked, canEditLocation, canOnlyEditLocationDetails, handleDismiss, flyToLocation, FormLayout, VsWrapper, VsButton, NewJobButton, FieldViewReadonlyCustomData, FieldViewLocationDetailsEdit, FieldViewLocationForm, FieldViewAdjustmentForm, FieldViewLocationInventory, FieldViewLocationNotFound, LocationDetailsJobTab, LocationDetailsSensorsTab, VsBottomSheet, VsText, LocationTagsEditor, TagsDisplay }
}

})